import { useUpdateProp } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DelayOption from 'client/components/core/Sidebar/components/Settings/options/DelayOption'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import ShadowStyle from 'client/components/core/Sidebar/components/Settings/styles/ShadowStyle'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { RowInterface } from 'common/components/entities/Row/types/RowInterface'
import { getMobileBackgroundFileId } from 'common/utils/getMobileBackgroundFileId'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { ImageControl } from 'src/editor/shared/settings/controls/image-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import { EntityBorderGroup } from 'src/editor/shared/settings/wrappers/border-groups/entity-border-group'

const RowSettings = ({
  entity,
  defaultEntity,
}: {
  entity: RowInterface
  defaultEntity?: RowInterface
}) => {
  const { isMobile } = useDeviceModeContext()
  const updateProp = useUpdateProp(entity)

  return (
    <div>
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        defaultPadding={
          isMobile ? defaultEntity?.mobilePadding : defaultEntity?.padding
        }
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
        withoutHorizontalLine
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={['backgroundColor', 'backgroundFileId']}
              mobileKeys={['mobileBackgroundColor', 'mobileBackgroundFileId']}
            />
          )
        }
      >
        page_settings.body.background.group_title
      </GroupTitle>
      <ColorPickerControl
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        isClearable
      />
      <ImageControl
        isMobile={isMobile}
        backgroundPosition={
          isMobile
            ? entity.mobileBackground || entity.background
            : entity.background
        }
        fileId={
          isMobile
            ? getMobileBackgroundFileId(
                entity.backgroundFileId,
                entity.mobileBackgroundFileId,
              )
            : entity.backgroundFileId
        }
        updateBackgroundFileId={
          isMobile
            ? updateProp('mobileBackgroundFileId')
            : updateProp('backgroundFileId')
        }
        update={
          isMobile ? updateProp('mobileBackground') : updateProp('background')
        }
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <EntityBorderGroup
        entity={entity}
        defaultEntity={defaultEntity}
        propertyKey="border"
        mobilePropertyKey="mobileBorder"
      />
      <DelayOption delay={entity.delay} update={updateProp('delay')} />
      <CheckboxControl
        label={'entity_settings.row.disable_stacking.label'}
        tooltipText={'entity_settings.row.disable_stacking.help_message'}
        value={entity.disableStacking}
        update={updateProp('disableStacking')}
      />
      <CheckboxControl
        label={'entity_settings.row.reverse_columns.label'}
        value={entity.reverseColumns}
        update={updateProp('reverseColumns')}
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </div>
  )
}

export default RowSettings
